import { useMemo, useSyncExternalStore } from 'react';
import { useServices } from '@/model';
import type { SessionLifetime, SessionService, SessionState } from '@/model';

const sourceIds = new WeakMap<SessionService, number>();
let nextId = 0;

const lifetimeKey = (value: SessionLifetime | null): string | null => value ? `${value.accountId}:${value.loginId}:${value.generation}` : null;

/** Captured callbacks may run after unmount, or before React commits a retirement. Fence both cases. */
export function captureLoginFence(session: SessionService): () => boolean {
  const state = session.state.get(), lifetime = session.lifetime, captured = lifetime.get(), key = lifetimeKey(captured);
  return () => {
    const current = session.state.get();
    return state.phase === 'signedIn' && current.phase === 'signedIn' && current.accountId === state.accountId
      && captured?.accountId === state.accountId && key !== null && lifetimeKey(lifetime.get()) === key;
  };
}

/** Observe every transition, including a logout/login pair React batches into one render. */
function observeSession(service: SessionService) {
  const state = service.state, lifetime = service.lifetime;
  let sourceId = sourceIds.get(service);
  if (sourceId === undefined) { sourceId = ++nextId; sourceIds.set(service, sourceId); }
  let previous = state.get(), previousLifetime = lifetime.get();
  const make = () => {
    const key = lifetimeKey(previousLifetime);
    const session: SessionState = previous.phase === 'signedIn' && previousLifetime?.accountId !== previous.accountId ? { phase: 'restoring' } : previous;
    return { session, key: `${sourceId}:${key}` };
  };
  let snapshot = make();
  const read = () => {
    const next = state.get(), nextLifetime = lifetime.get();
    if (next !== previous || nextLifetime !== previousLifetime) { previous = next; previousLifetime = nextLifetime; snapshot = make(); }
    return snapshot;
  };
  return { read, subscribe: (notify: () => void) => {
    const changed = () => { read(); notify(); };
    const stopState = state.subscribe(changed), stopLifetime = lifetime.subscribe(changed);
    return () => { stopState(); stopLifetime(); };
  } };
}

export function useLoginSession() {
  const services = useServices();
  const observer = useMemo(() => observeSession(services.session), [services]);
  return useSyncExternalStore(observer.subscribe, observer.read, observer.read);
}
